import {
  Building2,
  CalendarCheck,
  MapPin,
  NotebookPen,
  Package,
  Truck,
  UserRound,
} from 'lucide-react'

import ReschedulePopover from '@/components/deliveries/ReschedulePopover'
import type { DeliveryDto } from '@/services/deliveryService'
import { carrierLabel, isAwaitingArrival } from '@/services/deliveryService'
import { isLate, toClockTime } from '@/utils/scheduleTime'

import './DeliveryTile.css'

type Props = {
  delivery: DeliveryDto
  /** The page's shared clock — drives the Late tag. */
  now: Date
  onReschedule: (id: string, scheduledFor: string) => void
  /** Opens the full-record View / Edit dialog. */
  onEdit: (delivery: DeliveryDto) => void
}

function DeliveryTile({ delivery, now, onReschedule, onEdit }: Props) {
  /**
   * The one attention flag: the working ETA has passed and the delivery is
   * still expected. A nudge to check up on it if you can — once the truck is
   * on site it is not late, it is just here.
   */
  const late =
    isAwaitingArrival(delivery.stage) && isLate(delivery.scheduledFor, now)

  // The stripe encodes the same status the rail prints, so the two can never
  // disagree: maroon late > bright ETA > navy on site/unloading > green
  // received / maroon refused / gray cancelled.
  const statusModifier =
    delivery.stage === 'Received'
      ? 'received'
      : delivery.stage === 'Refused'
        ? 'refused'
        : delivery.stage === 'Cancelled'
          ? 'cancelled'
          : late
            ? 'late'
            : isAwaitingArrival(delivery.stage)
              ? 'incoming'
              : 'onsite'

  const rail = (
    <>
      <span className="delivery-tile__time">
        {toClockTime(delivery.scheduledFor)}
      </span>
      <span className="delivery-tile__status">{delivery.stage}</span>
      {late ? <span className="delivery-tile__late">Late</span> : null}
    </>
  )


  return (
    <article
      className={`delivery-tile delivery-tile--${statusModifier}`}
      aria-label={`${delivery.id} delivery tile`}
      data-flip-key={delivery.id}
    >
      {/* Only an expected delivery can be rescheduled -- a truck on site,
          unloading or closed gets the inert rail, no popover. */}
      {!isAwaitingArrival(delivery.stage) ? (
        <div className="delivery-tile__when">{rail}</div>
      ) : (
        <ReschedulePopover
          deliveryId={delivery.id}
          scheduledFor={delivery.scheduledFor}
          now={now}
          late={late}
          onReschedule={(scheduledFor) =>
            onReschedule(delivery.id, scheduledFor)
          }
        >
          {/* The whole rail is the trigger, not just the time: it gives touch
              a target well past 44px even at the narrow rail width. */}
          <button
            type="button"
            className="delivery-tile__when delivery-tile__when--action"
            aria-label={`Reschedule ${delivery.id}, currently ${toClockTime(delivery.scheduledFor)}`}
          >
            {rail}
          </button>
        </ReschedulePopover>
      )}

      <div className="delivery-tile__body">
        <div className="delivery-tile__identity">
          {/* The focal point: what the user reads off the truck coming down
              the road, so the row can be matched to it at a glance. h3
              because the day section owns the h2 — a day contains
              deliveries. */}
          <h3 className="delivery-tile__carrier">
            <Truck aria-hidden="true" />
            {carrierLabel(delivery.carrier)}
          </h3>

          {/* Outside the heading on purpose -- inside it, the sr-only text
              below became part of the heading's accessible name on a
              confirmed row ("Redline Logistics Call confirmed"), breaking
              heading navigation and any lookup by carrier name alone.
              Reserves the glyph's box whether or not the call is confirmed,
              so the row never reflows the moment it is -- visibility, not
              conditional mounting. aria-hidden mirrors that at the a11y
              layer instead of trusting the stylesheet's visibility rule to
              be loaded (it isn't under jsdom). */}
          <span
            className="delivery-tile__call-confirmed"
            data-visible={delivery.callConfirmed ? 'true' : undefined}
            aria-hidden={!delivery.callConfirmed}
          >
            <CalendarCheck aria-hidden="true" color="var(--fg-success)" />
            <span className="sr-only">Call confirmed</span>
          </span>

          {/* A missing BOL is pending paperwork, not an unknowable -- the
              ghost pill says "expected, not yet arrived" the way carriers'
              "shipment information sent" does. */}
          {delivery.bolNumber ? (
            <span className="delivery-tile__bol mono">
              <span className="sr-only">Bill of lading </span>
              {delivery.bolNumber}
            </span>
          ) : (
            <span className="delivery-tile__bol delivery-tile__bol--tbd">
              <span className="sr-only">Bill of lading </span>
              TBD
            </span>
          )}
          {/* Self-haul rows have no vendor line, so the order's PO sits here
              -- on these rows the carrier line *is* the vendor line. */}
          {delivery.vendor === delivery.carrier && delivery.bpoNumber ? (
            <span className="delivery-tile__po mono">
              <span className="sr-only">Blattner PO </span>
              {delivery.bpoNumber}
            </span>
          ) : null}
        </div>

        {/* Omitted on self-hauled loads — printing the same name twice is
            noise on a board that gets scanned rather than read. */}
        {delivery.vendor !== delivery.carrier ? (
          <p className="delivery-tile__vendor">
            <Building2 aria-hidden="true" />
            <span className="sr-only">Vendor</span>
            <span>{delivery.vendor}</span>
            {/* The order's document with the order's owner: PO by the
                vendor, as the BOL sits by the carrier. */}
            {delivery.bpoNumber ? (
              <span className="delivery-tile__po mono">
                <span className="sr-only">Blattner PO </span>
                {delivery.bpoNumber}
              </span>
            ) : null}
          </p>
        ) : null}

        <p className="delivery-tile__detail">
          <Package aria-hidden="true" />
          <span className="sr-only">Material</span>
          <span>{delivery.material}</span>
          {/* The cargo axis, beside the cargo. Appears once there is a
              verdict in motion -- Pending on every on-site row would be
              noise, and inbound rows have no cargo story at all. */}
          {delivery.cargoDisposition &&
          delivery.cargoDisposition !== 'Pending' ? (
            <span
              className={`delivery-tile__cargo delivery-tile__cargo--${delivery.cargoDisposition.toLowerCase()}`}
            >
              <span className="sr-only">Cargo </span>
              {delivery.cargoDisposition}
            </span>
          ) : null}
        </p>

        {delivery.note ? (
          <footer className="delivery-tile__footer">
            <p className="delivery-tile__note">
              <NotebookPen aria-hidden="true" />
              <span className="sr-only">Note</span>
              <span>{delivery.note}</span>
            </p>
          </footer>
        ) : null}
      </div>

      {/* Right column: who meets the truck and where it goes — the two
          things the check-in actually acts on. */}
      <ul className="delivery-tile__handling" aria-label="Handling">
        <li className="delivery-tile__detail">
          <UserRound aria-hidden="true" />
          <span className="sr-only">Receiver</span>
          <span>{delivery.driver}</span>
        </li>
        <li className="delivery-tile__detail">
          <MapPin aria-hidden="true" />
          <span className="sr-only">Destination</span>
          <span>{delivery.destination}</span>
        </li>
      </ul>

      {/* The slow path the rail popover's day-clamp routes to: full-record
          edit, cross-day moves, office cancel. */}
      <button
        type="button"
        className="btn btn--ghost delivery-tile__action"
        /* aria-label rather than an sr-only suffix: the accessible-name
           algorithm normalises each text node before joining them, so a
           space inside the hidden span is dropped and the name comes out
           "View / editDLV-1042". The visible text stays a subset of the
           label, which is what WCAG 2.5.3 asks for. */
        aria-label={`View / Edit ${delivery.id}`}
        onClick={() => onEdit(delivery)}
      >
        <span className="delivery-tile__action-label">
          <span>View /</span>
          <span>Edit</span>
        </span>
      </button>
    </article>
  )
}

export default DeliveryTile
